import { useEffect, useState } from 'react'
import { faChartLine } from '@fortawesome/pro-light-svg-icons'
import { ChartExporter } from '../chart-exporter'
import { ChartLoader } from '../chart-loader'
import { ControlBar } from '../control-bar'
import { LineBlockChart } from './line-block-chart'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { linePropService } from './line-prop.service'
import { lineDataService } from './line-data.service'
import { blockDataService } from '../block-data.service'
import { chartFlipperService } from '../chart-flipper.service'

export const LineBlock = function ({
  blockKey,
  dataKey,
  report,
  splitChart,
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  showTableToggle = true,
  ...props
}) {
  const [propOverrides, setPropOverrides] = useState()
  const [showExport, setShowExport] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [selections, setSelections] = useState()
  const [splitChartSelections, setSplitChartSelections] = useState(
    reportDataSetService.getFilterDefinitionSplitChartSelections(
      filterDefinition
    )
  )
  const [rawData, setRawData] = useState([])
  const [boundsX, setBoundsX] = useState()
  const [boundsY, setBoundsY] = useState()

  const [chartData, setChartData] = useState()
  const [tableData, setTableData] = useState()
  const [legendToggles, setLegendToggles] = useState({})

  const lineProps = linePropService.map(props, propOverrides)
  lineProps.boundsX = linePropService.mergeBounds(lineProps.boundsX, boundsX)
  lineProps.boundsY = linePropService.mergeBounds(lineProps.boundsY, boundsY)
  const currentStrata = linePropService.getStrataConfig(strataDefinition, selections?.strata?.value)

  const tableDefinition = chartFlipperService.applyTableDefinitionDefaults(
    {},
    {
      primaryLabel: lineProps.labelX ?? '',
      valueField: lineProps.dataDefinition.yField,
      valueFormat: lineProps.yFormat
    }
  )

  useEffect(() => {
    // Strata definition changes include legend labels - need to update them in legend & table flip
    updateChartData(rawData, selections)
  }, [strataDefinition, lineProps.scaleTypeX])

  useEffect(() => {
    updateBoundsY(rawData)
  }, [legendToggles])

  useEffect(() => {
    updateTableData(rawData, selections)
  }, [lineProps.yFormat])

  function onDataLoad (data) {
    data = splitChart ? data : [data]
    data = lineDataService.sortRawData(data, lineProps.dataDefinition.xField)
    data = lineDataService.removeEmptyValues(data, lineProps.dataDefinition.yField)

    updateChartData(data, selections)
    updateTableData(data, selections)
    setRawData(data)
    setLegendToggles({})
  }

  function updateChartData (data, selections) {
    if (!data?.length) {
      return
    }

    // The first dataset that is loaded determines the x-axis min/max so it stays consistent as dataset changes.
    if (!boundsX) {
      const boundsX = lineDataService.getMinMaxSorted(data, lineProps.dataDefinition.xField)
      if (boundsX && linePropService.isTimeSeries(lineProps.scaleTypeX)) {
        boundsX.min = lineDataService.convertToDate(boundsX.min)
        boundsX.max = lineDataService.convertToDate(boundsX.max)
      }
      setBoundsX(boundsX)
    }

    updateBoundsY(data)

    const chartData = data.reduce((chartData, data) => {
      if (sliderDefinition?.key) {
        data = blockDataService.filterBlockDataByKeyValue(data, sliderDefinition?.key, selections.slider)
      }

      chartData.push(lineDataService.toChartData(
        data,
        lineProps.dataDefinition,
        currentStrata,
        lineProps.scaleTypeX
      ))

      return chartData
    }, [])

    setChartData(chartData)
  }

  function updateBoundsY (data) {
    if (!data?.length) {
      return
    }

    // Series toggled off should not factor into y-axis min/max calculation
    const dataToggled = data.map((d) => d.filter((p) => {
      const series = p[currentStrata.value]
      const isSeriesOff = legendToggles[series] === true
      return !isSeriesOff
    }))
    const boundsY = lineDataService.getMinMaxUnsorted(dataToggled, [
      lineProps.dataDefinition.yField,
      lineProps.dataDefinition.yLoField,
      lineProps.dataDefinition.yHiField
    ])
    if (boundsY && linePropService.isTimeSeries(lineProps.scaleTypeY)) {
      boundsY.min = lineDataService.convertToDate(boundsY.min)
      boundsY.max = lineDataService.convertToDate(boundsY.max)
    }
    setBoundsY(boundsY)
  }

  function updateTableData (data, selections) {
    if (!showTableToggle || !data?.length) {
      return
    }

    const tableData = data.reduce((tableData, data) => {
      if (sliderDefinition?.key) {
        data = blockDataService.filterBlockDataByKeyValue(data, sliderDefinition?.key, selections.slider)
      }

      tableData.push(lineDataService.toTableData(
        data,
        lineProps.dataDefinition,
        currentStrata,
        tableDefinition,
        {
          isTimeSeries: linePropService.isTimeSeries(lineProps.scaleTypeX),
          xFormat: lineProps.xFormat,
          timeFormat: lineProps.timeFormat,
          scaleTypeX: lineProps.scaleTypeX
        }
      ))

      return tableData
    }, [])

    setTableData(tableData)
  }

  function onControlBarChange (selections, changes) {
    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider) {
      updateChartData(rawData, selections)
    } else {
      setSelections(selections)
    }
  }

  function onControlBarToggle ({ type, on }) {
    switch (type) {
      case 'export':
        setShowExport(on)
        break
      case 'table':
        setShowTable(on)
        break
    }
  }

  return (
    <div className="report-full-width">
      <ControlBar
        strataDefinition={strataDefinition}
        sliderDefinition={sliderDefinition}
        filterDefinition={filterDefinition}
        parentControlBar={parentControlBar}
        parentBlock={parentBlock}
        parentBlockSelections={parentBlockSelections}
        tableToggle={{
          enabled: showTableToggle,
          on: showTable,
          iconOn: faChartLine,
          tooltipOn: 'Show Chart'
        }}
        exportToggle={{
          enabled: true,
          on: showExport
        }}
        onChange={onControlBarChange}
        onToggle={onControlBarToggle}
      />
      <ChartLoader
        report={report}
        blockKey={blockKey}
        dataKey={dataKey}
        sliderDefinition={sliderDefinition}
        selections={selections}
        splitChart={splitChart}
        splitChartSelections={splitChartSelections}
        onDataLoad={onDataLoad}
      >
        <LineBlockChart
          dataKey={dataKey}
          chartData={chartData}
          tableData={tableData}
          strata={currentStrata}
          legendToggles={legendToggles}
          showTableToggle={showTableToggle}
          showTable={showTable}
          tableDefinition={tableDefinition}
          filterDefinition={filterDefinition}
          onLegendToggle={setLegendToggles}
          onSplitFilterChange={setSplitChartSelections}
          splitChart={splitChart}
          {...lineProps}
        />
      </ChartLoader>
      <ChartExporter
        selections={selections}
        isOpen={showExport}
        onClose={() => setShowExport(false)}
      >
        <LineBlockChart
          dataKey={dataKey}
          chartData={chartData}
          tableData={tableData}
          strata={currentStrata}
          legendToggles={legendToggles}
          showTableToggle={showTableToggle}
          showTable={showTable}
          tableDefinition={tableDefinition}
          filterDefinition={filterDefinition}
          onLegendToggle={setLegendToggles}
          onSplitFilterChange={setSplitChartSelections}
          splitChart={splitChart}
          exporting={true}
          {...lineProps}
        />
      </ChartExporter>
    </div>
  )
}
